import React, { useMemo } from 'react'
import NextLink from 'next/link'
import clsx from 'clsx'

import { Icon } from '@wartek-id/icon-v2'
import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'

import { PraktikKinerjaStepCard } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'

import type { StepCardStatus } from 'app/KinerjaShared/components/PraktikKinerjaStepCard'
import type { StepProgressData } from 'app/KinerjaShared/components/PraktikKinerjaPegawaiStepCards/types'

type RefleksiEvaluasiStepCardProps<TStep extends string = string> = {
  className?: string
  detailHref: string
  description: React.ReactNode
  enableCompletedEdit?: boolean
  editButtonLabel?: string
  imagePath: string
  lockedHint?: string
  progressData?: StepProgressData<TStep>
  sortedSteps: Array<TStep>
  step: TStep
  title: React.ReactNode
  onClickEdit?: (step: TStep) => void
}

export function RefleksiEvaluasiStepCard<TStep extends string = string>({
  className,
  description,
  detailHref,
  enableCompletedEdit = true,
  editButtonLabel = 'Ubah',
  imagePath,
  lockedHint = 'Menunggu aksi dari Tim Kinerja.',
  progressData = {},
  sortedSteps,
  step,
  title,
  onClickEdit,
}: RefleksiEvaluasiStepCardProps<TStep>) {
  const handleEditClick = () => {
    if (typeof onClickEdit !== 'function') return

    onClickEdit(step)
  }

  const stepCardStatus: StepCardStatus = useMemo(() => {
    const currentStepIndex = sortedSteps.indexOf(progressData.step)
    const refleksiStepIndex = sortedSteps.indexOf(step)

    const appealStepIndex = sortedSteps.indexOf(progressData.appealStep)
    const isAppealingPriorSteps =
      !!progressData.appealStep &&
      appealStepIndex >= 0 &&
      appealStepIndex < refleksiStepIndex

    if (currentStepIndex > refleksiStepIndex) return 'completed'
    if (currentStepIndex === refleksiStepIndex && !isAppealingPriorSteps)
      return 'active'

    return 'locked'
  }, [progressData.appealStep, progressData.step, sortedSteps, step])

  const showPrimaryLink = ['active', 'completed'].includes(stepCardStatus)

  const primaryLabel = useMemo(() => {
    if (stepCardStatus === 'active') {
      return progressData.latestFormState === 'DRAFT' ? 'Lanjutkan' : 'Lakukan'
    }

    return 'Cek rincian'
  }, [progressData.latestFormState, stepCardStatus])

  const isAppealing = progressData.appealStep === step
  const showAppealSecondary =
    enableCompletedEdit && stepCardStatus === 'completed'

  const isAppealRollbackDisabled =
    !!progressData.appealStep && progressData.appealStep !== step

  return (
    <PraktikKinerjaStepCard
      className={className}
      actions={
        <div className="flex gap-2 flex-wrap">
          {showPrimaryLink ? (
            <NextLink href={detailHref} passHref>
              <Button
                as="a"
                className="!inline-flex"
                color={stepCardStatus === 'active' ? 'blue' : 'white'}
                size="lg"
                type={undefined}
              >
                {primaryLabel}
              </Button>
            </NextLink>
          ) : null}

          {showAppealSecondary ? (
            isAppealing ? (
              <div className="flex gap-1 items-center py-3 animate-pulse">
                <Icon
                  color="informational"
                  className="motion-safe:animate-spin"
                >
                  hourglass_empty
                </Icon>
                <Text className="!text-blue-60" color="informational">
                  Perubahan sedang diajukan.
                </Text>
              </div>
            ) : (
              <Button
                className={clsx(
                  'gap-1 !bg-white',
                  isAppealRollbackDisabled && '!text-blue-40'
                )}
                color="blue"
                disabled={isAppealRollbackDisabled}
                size="lg"
                variant="ghost"
                onClick={handleEditClick}
              >
                <Icon color="inherit">edit_outline</Icon>
                {editButtonLabel}
              </Button>
            )
          ) : null}
        </div>
      }
      description={description}
      iconSrc={imagePath}
      lockedHint={lockedHint}
      status={stepCardStatus}
      title={title}
    />
  )
}
